// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Message} from '@app/features/messaging/models/MessagingMessage';
import type {ChannelStreamItem} from '@app/features/messaging/utils/MessageGroupingUtils';
import {MESSAGE_LAYOUT_SPEC} from '@app/features/theme/layout/MessageLayoutSpec';

export function getUnreadDividerBeforeMessageId(
	pendingStreamItems: ReadonlyArray<ChannelStreamItem>,
	suppressUnreadIndicator?: boolean,
): string | null {
	if (suppressUnreadIndicator) {
		return null;
	}
	for (const item of pendingStreamItems) {
		if (item.showUnreadDividerBefore) {
			return (item.content as Message).id;
		}
	}
	return null;
}

const ROOT_FONT_PX = 16;

function remToPx(remValue: `${number}rem`): number {
	return Number.parseFloat(remValue) * ROOT_FONT_PX;
}

/**
 * One visible reaction row: container gap + reactionsGrid padding-top + chip
 * (border + inner padding + 1.25rem emoji with 0.125rem margins).
 */
const REACTION_GRID_PADDING_TOP: `${number}rem` = '0.25rem';
const REACTION_CHIP_HEIGHT: `${number}rem` = '1.875rem';

/** Cozy message layout tokens in px (16px root). Used for costume headroom lower bounds. */
export const COSTUME_HEADROOM_LAYOUT_PX = {
	lineHeight: remToPx(MESSAGE_LAYOUT_SPEC.lineHeight),
	spacingY: remToPx(MESSAGE_LAYOUT_SPEC.spacingY),
	avatarSize: remToPx(MESSAGE_LAYOUT_SPEC.cozy.avatarSize),
	avatarAlignOffset: Math.min(
		Math.max(0, (remToPx(MESSAGE_LAYOUT_SPEC.lineHeight) * 2 - remToPx(MESSAGE_LAYOUT_SPEC.cozy.avatarSize)) / 2),
		remToPx('0.5rem'),
	),
	containerGap: remToPx(MESSAGE_LAYOUT_SPEC.containerGap),
	reactionRow:
		remToPx(MESSAGE_LAYOUT_SPEC.containerGap) + remToPx(REACTION_GRID_PADDING_TOP) + remToPx(REACTION_CHIP_HEIGHT),
} as const;

/**
 * Guaranteed lower-bound gap (px) from the previous costumed avatar's bottom to this
 * avatar's top. Returns null when nothing above has a cozy group-header avatar
 * (stream start, divider, blocked group).
 *
 * Adds a small contact slack so tips can sit flush with the avatar above instead
 * of stopping short and looking more scrunched than needed.
 */
export function estimateCostumeHeadroomPx(input: {
	previousGroupMessageCount: number | null;
	spacerPx: number;
	/** Messages in the previous group that show at least one reaction row. */
	previousGroupReactionRows?: number;
}): number | null {
	const count = input.previousGroupMessageCount;
	if (count == null || count < 1) return null;

	const {lineHeight, spacingY, avatarSize, avatarAlignOffset, reactionRow} = COSTUME_HEADROOM_LAYOUT_PX;
	const headerRowMin = spacingY + (lineHeight + lineHeight) + spacingY;
	const avatarTopInset = spacingY + avatarAlignOffset;
	const belowAvatarInHeader = Math.max(0, headerRowMin - avatarTopInset - avatarSize);
	const continuationRowMin = spacingY + lineHeight + spacingY;
	const belowAvatarInGroup = belowAvatarInHeader + (count - 1) * continuationRowMin;
	const aboveNextAvatar = avatarTopInset;
	const spacerPx = Math.max(0, input.spacerPx);
	const reactionRows = Math.max(0, input.previousGroupReactionRows ?? 0);
	/** Allow the tip to meet the avatar above instead of leaving a scrunch gap. */
	const contactSlackPx = 8;
	return belowAvatarInGroup + spacerPx + aboveNextAvatar + contactSlackPx + reactionRows * reactionRow;
}
